import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import messageService from '../../services/messageService';
import { onCallInvite, onCallSignal, sendCallInvite, sendCallSignal, endCallInvite } from '../../services/messagesRealtime';
import { useAuth, useUser } from '../../context';
import PeerMesh from './peerMesh';
import { RING_MS, MAX_VIDEO, MAX_VOICE, outcomeStatus, endedLabel, PERM_DENIED_TOAST, roomFor, fmtTimer } from './callModel';
import { nativeCall, nativePlatform, onNativeCall, getPendingNative, registerVoipToken } from '../../utils/nativeCall';
import { createPortalRinger } from './portalRingtone';

const CallContext = createContext(null);

/** The call, from anywhere. Null outside the provider (tests, Noir). */
export function useCall() { return useContext(CallContext); }

/**
 * A call id the hub can bind as a Guid. `crypto.randomUUID` isn't on every WebView, and the
 * old fallback here was not a GUID at all — the hub refused every call made from a browser
 * without it ("The JSON value could not be converted to System.Guid").
 */
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function uuid() {
  // Privacy extensions and some browsers stub randomUUID to return nothing — the hub then
  // got `null` for the call id and refused the call. Trust the value, not the function.
  if (window.crypto?.randomUUID) {
    try { const v = window.crypto.randomUUID(); if (typeof v === 'string' && GUID_RE.test(v)) return v; } catch { /* stubbed */ }
  }
  const b = new Uint8Array(16);
  if (window.crypto?.getRandomValues) window.crypto.getRandomValues(b); else for (let i = 0; i < 16; i += 1) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
const PERM_KEY = 'pr.call.perm';            // the first-call permission card was shown
const CAM_KEY = 'pr.call.cam';              // the camera card was shown
const CORNER_KEY = 'pr.call.corner';        // where the floating window last sat

/**
 * One call controller for the whole app (design_handoff_messages Part B). Mounted above the
 * router, so navigating never ends a call: the full screen, the minimized bar / window and
 * the top-nav pill are all views of the state held here.
 *
 *   state: { id, threadId, dir: 'out'|'in', video, phase: perm|calling|ringing|incoming|connecting|live|ended,
 *            view: 'full'|'mini', muted, camOn, speaker, facing, route, startedAt,
 *            peers: [{ userId, name, avatarUrl, state: ringing|joined|left, level, cameraPaused, muted }],
 *            from, then, origin, miniPos: { corner }, endedLabel }
 *
 * Timers run off `startedAt`, never off accumulated ticks. Audio levels come off an
 * AnalyserNode at ~6 Hz (PeerMesh). The caller's client posts the ONE record of the call.
 */
export default function CallProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const { profile } = useUser();
  const myId = profile?.userId || null;
  // Dial/answer handlers live longer than the first render (when the profile is still
  // null). Read the current account at the action boundary, then use that same identity
  // for the invite and the mesh. Capturing myId here sent peers: [null, recipientId].
  const profileRef = useRef(profile);
  profileRef.current = isAuthenticated ? profile : null;
  const [call, setCall] = useState(null);
  const [nativeAnswerId, setNativeAnswerId] = useState(null);
  const [toast, setToast] = useState(null);
  const [now, setNow] = useState(Date.now());
  // Remote streams by peer id; a version bump re-renders the <video>/<audio> elements.
  const streamsRef = useRef(new Map());
  const [streamVersion, setStreamVersion] = useState(0);
  const localStreamRef = useRef(null);
  const meshRef = useRef(null);
  const mediaAttempt = useRef(0);
  const callRef = useRef(call);
  callRef.current = call;
  const timers = useRef([]);
  const toastTimer = useRef(null);
  // Where the full screen opens from / closes into (a header button, CALL BACK, the mini bar).
  const originRef = useRef(null);
  // The thread column on desktop: the full screen fills it instead of the whole viewport.
  const anchorRef = useRef(null);
  const ringerRef = useRef(null);

  useEffect(() => {
    const ringer = createPortalRinger();
    ringerRef.current = ringer;
    document.addEventListener('pointerdown', ringer.unlock);
    document.addEventListener('keydown', ringer.unlock);
    return () => {
      document.removeEventListener('pointerdown', ringer.unlock);
      document.removeEventListener('keydown', ringer.unlock);
      ringer.dispose();
    };
  }, []);
  useEffect(() => {
    const mode = call?.phase === 'incoming' && !nativePlatform() ? 'incoming'
      : call?.dir === 'out' && (call?.phase === 'ringing' || (call?.phase === 'calling' && call?.inviteSent)) ? 'outgoing' : null;
    ringerRef.current?.setMode(mode);
  }, [call?.phase, call?.dir, call?.inviteSent]);

  const flash = useCallback((text, ms = 2600) => {
    clearTimeout(toastTimer.current);
    setToast(text);
    toastTimer.current = setTimeout(() => setToast(null), ms);
  }, []);
  const later = (ms, fn) => { const t = setTimeout(fn, ms); timers.current.push(t); return t; };
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const patch = useCallback((p) => setCall((c) => (c ? { ...c, ...(typeof p === 'function' ? p(c) : p) } : c)), []);
  const patchPeer = useCallback((userId, p) => patch((c) => ({ peers: c.peers.map((x) => (x.userId === userId ? { ...x, ...(typeof p === 'function' ? p(x) : p) } : x)) })), [patch]);

  // The live clock, only while something on screen counts.
  useEffect(() => {
    if (!call || (call.phase !== 'live' && call.phase !== 'incoming')) return undefined;
    const iv = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(iv);
  }, [call?.phase, !!call]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Media ──

  const stopLocal = () => {
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
  };

  /** Mic (and camera): the browser's prompt, after our own card the first time. */
  const getMedia = async ({ video, facing = 'user' }) => {
    const constraints = { audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: video ? { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } } : false };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    return stream;
  };

  const teardown = () => {
    mediaAttempt.current += 1;
    ringerRef.current?.setMode(null);
    clearTimers();
    meshRef.current?.close();
    meshRef.current = null;
    stopLocal();
    streamsRef.current = new Map();
    setStreamVersion((v) => v + 1);
  };

  const buildMesh = async (c, selfId) => {
    const ice = await messageService.getIceServers();
    const iceServers = ice.success ? (ice.data?.iceServers || []).map((s) => ({ urls: s.urls, username: s.username || undefined, credential: s.credential || undefined })) : [{ urls: 'stun:stun.l.google.com:19302' }];
    const mesh = new PeerMesh({
      myId: selfId,
      iceServers,
      send: (peerId, type, payload) => sendCallSignal(c.id, c.threadId, peerId, type, payload).catch(() => {}),
      onTrack: (peerId, stream) => { streamsRef.current.set(peerId, stream); setStreamVersion((v) => v + 1); },
      onPeerState: (peerId, state) => {
        if (state === 'connected') {
          ringerRef.current?.setMode(null);
          patch((cc) => ({
            phase: 'live',
            startedAt: cc.startedAt || Date.now(),
            peers: cc.peers.map((p) => (p.userId === peerId ? { ...p, state: 'joined' } : p)),
          }));
        } else if (state === 'failed' || state === 'closed') {
          // A peer that fell off: drop them; the last one gone ends the call.
          const cc = callRef.current;
          if (cc && cc.peers.some((p) => p.userId === peerId && p.state === 'joined')) peerLeft(peerId);
        }
      },
      onLevels: (levels) => patch((cc) => ({ selfLevel: levels.me || 0, peers: cc.peers.map((p) => ({ ...p, level: levels[p.userId] || 0 })) })),
      onRoute: (route) => patch({ route }),
    });
    if (localStreamRef.current) mesh.setLocalStream(localStreamRef.current);
    meshRef.current = mesh;
    return mesh;
  };

  // ── Ending ──

  const finish = useCallback(async ({ reason, notify = true }) => {
    const c = callRef.current;
    if (!c) return;
    const wasLive = c.phase === 'live' && !!c.startedAt;
    const durationSec = wasLive ? Math.floor((Date.now() - c.startedAt) / 1000) : 0;
    const status = outcomeStatus({ wasLive, reason });
    if (notify) c.peers.forEach((p) => { if (p.state !== 'left') sendCallSignal(c.id, c.threadId, p.userId, 'hangup').catch(() => {}); });
    if (c.dir === 'out') endCallInvite(c.id);
    // The system call closes with ours: ours when we hung up, theirs / unanswered otherwise.
    nativeCall.ended({ callId: c.id, reason: reason === 'hangup' ? (wasLive ? 'ended' : 'cancelled') : reason === 'remote' ? 'remote' : status });
    teardown();
    setCall(null);
    // One record per call, from the caller. The thread hears it live.
    if (c.dir === 'out' && c.threadId) {
      const people = c.peers.filter((p) => p.state !== 'ringing' || status === 'noanswer').length + 1;
      await messageService.postCallRecord(c.threadId, { callId: c.id, video: !!c.video, status, durationSec, people: Math.max(2, people) });
    }
    return endedLabel(status, durationSec);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const peerLeft = (peerId) => {
    const c = callRef.current;
    if (!c) return;
    meshRef.current?.removePeer(peerId);
    streamsRef.current.delete(peerId);
    setStreamVersion((v) => v + 1);
    const remaining = c.peers.filter((p) => p.userId !== peerId && p.state !== 'left');
    if (remaining.length === 0) {
      // Everyone else is gone. The caller posts; a callee just closes.
      finish({ reason: c.phase === 'live' ? 'hangup' : 'declined', notify: false });
      return;
    }
    patchPeer(peerId, { state: 'left' });
  };

  // ── Starting ──

  const needsCard = (key) => { try { return !localStorage.getItem(key); } catch { return false; } };
  const markCard = (key) => { try { localStorage.setItem(key, '1'); } catch { /* private mode */ } };

  /**
   * Dial. `peers` are the people to ring ({ userId, name, avatarUrl }); `originEl` is what was
   * tapped (the portal opens from it and closes back into it).
   */
  const start = useCallback(async ({ threadId, video = false, peers = [], originEl = null }) => {
    const existing = callRef.current;
    if (existing) {
      if (existing.view === 'mini') { originRef.current = originEl || null; patch({ view: 'full' }); return; }
      flash('YOU’RE ALREADY ON A CALL');
      return;
    }
    if (!peers.length || !threadId) return;
    if (!profileRef.current?.userId) { flash('PROFILE IS STILL LOADING · TRY AGAIN'); return; }
    if (peers.some((p) => !p?.userId)) { flash('COULDN’T FIND WHO TO CALL · REOPEN THIS CHAT'); return; }
    if (peers.length + 1 > (video ? MAX_VIDEO : MAX_VOICE)) { flash(roomFor({ video, peers }).full); return; }
    originRef.current = originEl ? rectCentre(originEl) : null;
    const base = {
      id: uuid(), threadId, dir: 'out', video, camOn: video, muted: false, speaker: video, facing: 'user', route: 'direct',
      view: 'full', startedAt: null, selfLevel: 0, adding: false,
      peers: peers.map((p) => ({ userId: p.userId, name: p.name || p.displayName || '', avatarUrl: p.avatarUrl || null, state: 'ringing', level: 0, cameraPaused: false })),
      miniPos: { corner: readCorner() },
    };
    if (!nativePlatform() && needsCard(PERM_KEY)) { setCall({ ...base, phase: 'perm', then: 'dial' }); return; }
    setCall({ ...base, phase: 'calling' });
    await dial(base);
  }, [flash, patch]); // eslint-disable-line react-hooks/exhaustive-deps

  const dial = async (c) => {
    const selfId = profileRef.current?.userId;
    if (!selfId) { setCall(null); flash('PROFILE IS STILL LOADING · TRY AGAIN'); return; }
    if (!GUID_RE.test(c.id || '')) { c.id = uuid(); patch({ id: c.id }); }
    const attempt = ++mediaAttempt.current;
    try {
      await nativeCall.ensureMediaPermissions({ video: c.video });
      if (attempt !== mediaAttempt.current) return;
      // OS permission first, native audio setup second, WebView capture third.
      await nativeCall.startOutgoing({ callId: c.id, name: c.peers.map((p) => p.name).filter(Boolean).join(', ') || 'Portal', video: c.video });
      if (attempt !== mediaAttempt.current) return;
      const stream = await getMedia({ video: c.video, facing: c.facing || 'user' });
      if (attempt !== mediaAttempt.current) { stream.getTracks().forEach((track) => track.stop()); return; }
      localStreamRef.current = stream;
    } catch (err) {
      if (attempt !== mediaAttempt.current) return;
      nativeCall.ended({ callId: c.id, reason: 'failed' });
      setCall(null);
      flash(err?.code === 'CAMERA_PERMISSION_DENIED' ? 'VIDEO CALLS NEED THE CAMERA · ENABLE IT IN SETTINGS' : PERM_DENIED_TOAST);
      return;
    }
    // Permission granted at least once: the card isn't shown again.
    markCard(PERM_KEY); if (c.video) markCard(CAM_KEY);
    await buildMesh(c, selfId);
    try {
      await sendCallInvite(c.id, c.threadId, c.video, c.peers.map((p) => p.userId), [selfId, ...c.peers.map((p) => p.userId)]);
    } catch (err) {
      // Say what went wrong: the hub's own words when it refused, else the connection.
      const hubMsg = (err?.message || '').replace(/^.*HubException: /, '').replace(/^An unexpected error occurred invoking '\w+' on the server\.?/, '').trim();
      console.warn('call invite failed', err);
      nativeCall.ended({ callId: c.id, reason: 'failed' });
      teardown(); setCall(null);
      flash(hubMsg && !/Not connected/i.test(hubMsg) ? `COULDN’T CALL · ${hubMsg.toUpperCase().slice(0, 60)}` : 'COULDN’T REACH THEM · CHECK YOUR CONNECTION');
      return;
    }
    // The callee can answer before SignalR acknowledges this invocation. An acknowledgement
    // only marks delivery; it must never move an answered/live call back into CALLING.
    patch((cc) => (cc.id === c.id ? { inviteSent: true } : {}));
    later(RING_MS, () => {
      const cc = callRef.current;
      if (cc && cc.id === c.id && (cc.phase === 'calling' || cc.phase === 'ringing')) finish({ reason: 'noanswer' });
    });
  };

  // ── Incoming ──

  const incoming = useCallback((inv, { answered = false } = {}) => {
    const c = callRef.current;
    if (!myId || inv.from?.userId === myId) return;
    if (c) {
      // Already on this call (a second device, or an ADD re-sent to us) or another: say so.
      if (c.id !== inv.callId) sendCallSignal(inv.callId, inv.threadId, inv.from.userId, 'busy').catch(() => {});
      return;
    }
    // Too old to ring (the push came late): leave it; the caller has moved on.
    if (inv.sentAt && Date.now() - new Date(inv.sentAt).getTime() > RING_MS) return;
    originRef.current = null;
    const others = (inv.peers || []).filter((id) => id !== myId && id !== inv.from.userId);
    setCall({
      id: inv.callId, threadId: inv.threadId, dir: 'in', video: !!inv.video, camOn: !!inv.video, muted: false, speaker: !!inv.video, facing: 'user', route: 'direct',
      phase: 'incoming', view: 'full', startedAt: null, selfLevel: 0, adding: false,
      from: inv.from,
      peers: [
        { userId: inv.from.userId, name: inv.from.displayName || '', avatarUrl: inv.from.avatarUrl || null, state: 'joined', level: 0, cameraPaused: false },
        ...others.map((id) => ({ userId: id, name: '', avatarUrl: null, state: 'joined', level: 0, cameraPaused: false })),
      ],
      miniPos: { corner: readCorner() },
    });
    if (answered) nativeCall.incomingHandled();
    else {
      sendCallSignal(inv.callId, inv.threadId, inv.from.userId, 'ringing').catch(() => {});
      nativeCall.reportIncoming(inv);
    }
    if (!nativePlatform()) { try { navigator.vibrate?.([300, 200, 300]); } catch { /* no vibration */ } }
    const age = inv.sentAt ? Date.now() - new Date(inv.sentAt).getTime() : 0;
    later(Math.max(1000, RING_MS - age), () => {
      const cc = callRef.current;
      if (cc && cc.id === inv.callId && cc.phase === 'incoming') { nativeCall.ended({ callId: inv.callId, reason: 'noanswer' }); nativeCall.incomingHandled(); teardown(); setCall(null); }
    });
  }, [myId]); // eslint-disable-line react-hooks/exhaustive-deps

  const accept = useCallback(async () => {
    const c = callRef.current;
    if (!c || c.phase !== 'incoming') return;
    if (!nativePlatform() && needsCard(PERM_KEY)) { patch({ phase: 'perm', then: 'accept' }); return; }
    await join(c);
  }, [patch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Native "incoming" and "answer" may arrive in the same event before React commits.
  // Wait for the invite to be in state instead of dropping Answer or guessing a timeout.
  useEffect(() => {
    if (nativeAnswerId && call?.id === nativeAnswerId && call.phase === 'incoming') {
      setNativeAnswerId(null);
      accept();
    }
  }, [nativeAnswerId, call?.id, call?.phase, accept]);

  const join = async (c) => {
    const self = profileRef.current;
    if (!self?.userId) { flash('PROFILE IS STILL LOADING · TRY AGAIN'); return; }
    clearTimers();
    nativeCall.incomingHandled();
    patch({ phase: 'connecting' });
    const attempt = ++mediaAttempt.current;
    try {
      await nativeCall.ensureMediaPermissions({ video: c.video });
      if (attempt !== mediaAttempt.current) return;
      await nativeCall.answerIncoming?.({ video: c.video });
      if (attempt !== mediaAttempt.current) return;
      const stream = await getMedia({ video: c.video, facing: 'user' });
      if (attempt !== mediaAttempt.current) { stream.getTracks().forEach((track) => track.stop()); return; }
      localStreamRef.current = stream;
    } catch (err) {
      if (attempt !== mediaAttempt.current) return;
      sendCallSignal(c.id, c.threadId, c.from.userId, 'decline').catch(() => {});
      nativeCall.ended({ callId: c.id, reason: 'failed' });
      teardown(); setCall(null); flash(err?.code === 'CAMERA_PERMISSION_DENIED' ? 'VIDEO CALLS NEED THE CAMERA · ENABLE IT IN SETTINGS' : PERM_DENIED_TOAST);
      return;
    }
    markCard(PERM_KEY); if (c.video) markCard(CAM_KEY);
    const mesh = await buildMesh(c, self.userId);
    const me = { displayName: self.displayName || '', avatarUrl: self.avatarUrl || null };
    // Tell everyone on the call we're in; the one with the smaller id offers (PeerMesh).
    for (const p of c.peers) {
      mesh.addPeer(p.userId);
      sendCallSignal(c.id, c.threadId, p.userId, 'accept', me).catch(() => {});
    }
  };

  const decline = useCallback(async (quickReply = null) => {
    const c = callRef.current;
    if (!c || (c.phase !== 'incoming' && !(c.phase === 'perm' && c.then === 'accept'))) return;
    sendCallSignal(c.id, c.threadId, c.from.userId, 'decline').catch(() => {});
    nativeCall.incomingHandled();
    nativeCall.ended({ callId: c.id, reason: 'ended' });   // we ended it, on our side
    teardown(); setCall(null);
    if (quickReply && c.threadId) messageService.sendMessage(c.threadId, { kind: 'text', body: quickReply }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hangup = useCallback(() => {
    const c = callRef.current;
    if (!c) return;
    if (c.phase === 'incoming') { decline(); return; }
    finish({ reason: 'hangup' });
  }, [decline, finish]);

  // ── Signals in ──

  useEffect(() => {
    if (!isAuthenticated || !myId) return undefined;
    const offInvite = onCallInvite(incoming);
    const offSignal = onCallSignal((sig) => {
      const c = callRef.current;
      if (!c || sig.callId !== c.id) {
        // A late hangup for a call we already left is fine; anything else is noise.
        return;
      }
      const from = sig.fromUserId;
      const payload = sig.payload ? safeParse(sig.payload) : null;
      switch (sig.type) {
        case 'handled-elsewhere':
          if (from === myId && (c.phase === 'incoming' || (c.phase === 'perm' && c.then === 'accept'))) {
            nativeCall.incomingHandled();
            nativeCall.ended({ callId: c.id, reason: 'ended' });
            teardown();
            setCall(null);
          }
          break;
        case 'ringing':
          // Guard against a late ringing event in the same React batch as accept.
          patch((cc) => (cc.phase === 'calling' ? { phase: 'ringing' } : {}));
          break;
        case 'accept': {
          ringerRef.current?.setMode(null);
          // They're in. Name them if we didn't know them (someone else's add), connect.
          clearTimers();
          patch((cc) => {
            const known = cc.peers.some((p) => p.userId === from);
            const peers = known
              ? cc.peers.map((p) => (p.userId === from ? { ...p, state: 'connecting', name: p.name || payload?.displayName || '', avatarUrl: p.avatarUrl || payload?.avatarUrl || null } : p))
              : [...cc.peers, { userId: from, name: payload?.displayName || '', avatarUrl: payload?.avatarUrl || null, state: 'connecting', level: 0, cameraPaused: false }];
            return { peers, phase: cc.phase === 'live' ? 'live' : 'connecting' };
          });
          meshRef.current?.addPeer(from);
          break;
        }
        case 'decline':
        case 'busy': {
          if (sig.type === 'busy') flash('THEY’RE ON ANOTHER CALL');
          const others = c.peers.filter((p) => p.userId !== from && p.state !== 'left');
          if (others.length === 0) finish({ reason: 'declined', notify: false });
          else patchPeer(from, { state: 'left' });
          break;
        }
        case 'offer':
        case 'answer':
        case 'ice':
          meshRef.current?.handleSignal(from, sig.type, payload);
          break;
        case 'hangup':
          peerLeft(from);
          break;
        case 'camera-paused': patchPeer(from, { cameraPaused: true }); break;
        case 'camera-resumed': patchPeer(from, { cameraPaused: false }); break;
        case 'mute': patchPeer(from, { muted: !!payload?.muted }); break;
        default: break;
      }
    });
    return () => { offInvite(); offSignal(); };
  }, [isAuthenticated, myId, incoming, patch, patchPeer, finish, flash]); // eslint-disable-line react-hooks/exhaustive-deps

  // Signed out: the call goes too.
  useEffect(() => { if (!isAuthenticated && callRef.current) { teardown(); setCall(null); } }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Controls ──

  const toggleMute = useCallback(() => {
    const c = callRef.current;
    if (!c) return;
    const muted = !c.muted;
    localStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = !muted; });
    patch({ muted });
    nativeCall.setMuted({ callId: c.id, muted });
    c.peers.forEach((p) => sendCallSignal(c.id, c.threadId, p.userId, 'mute', { muted }).catch(() => {}));
  }, [patch]);

  /** From the OS (CallKit's mute, the notification's MUTE): land on the value, or flip. */
  const setMutedFromNative = useCallback((muted) => {
    const c = callRef.current;
    if (!c) return;
    const next = muted == null ? !c.muted : !!muted;
    if (next === c.muted) return;
    localStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = !next; });
    patch({ muted: next });
    c.peers.forEach((p) => sendCallSignal(c.id, c.threadId, p.userId, 'mute', { muted: next }).catch(() => {}));
  }, [patch]);

  const turnCameraOn = async (c) => {
    try {
      await nativeCall.ensureMediaPermissions({ video: true });
      if (callRef.current?.id !== c.id) return;
      const cam = await navigator.mediaDevices.getUserMedia({ video: { facingMode: c.facing || 'user', width: { ideal: 1280 }, height: { ideal: 720 } } });
      if (callRef.current?.id !== c.id) { cam.getTracks().forEach((track) => track.stop()); return; }
      const track = cam.getVideoTracks()[0];
      if (!localStreamRef.current) localStreamRef.current = new MediaStream();
      await meshRef.current?.setVideoTrack(track);
      if (!meshRef.current) localStreamRef.current.addTrack(track);
      markCard(CAM_KEY);
      patch({ video: true, camOn: true, speaker: true });
      nativeCall.setSpeaker(true);
      c.peers.forEach((p) => sendCallSignal(c.id, c.threadId, p.userId, 'camera-resumed').catch(() => {}));
    } catch {
      patch({ phase: c.phase === 'perm' ? 'live' : c.phase });
      flash('CAMERA STAYS OFF');
    }
  };

  const toggleCamera = useCallback(async () => {
    const c = callRef.current;
    if (!c) return;
    if (!c.video || !c.camOn) {
      if (!c.video && c.peers.filter((p) => p.state !== 'left').length + 1 > MAX_VIDEO) { flash(`VIDEO IS UP TO ${MAX_VIDEO} PEOPLE`); return; }
      if (!nativePlatform() && !c.video && needsCard(CAM_KEY)) { patch({ phase: 'perm', then: 'camera', resumePhase: c.phase }); return; }
      await turnCameraOn(c);
      return;
    }
    // Camera off: keep the track so the slot stays, stop sending frames.
    localStreamRef.current?.getVideoTracks().forEach((t) => { t.enabled = false; });
    patch({ camOn: false });
    c.peers.forEach((p) => sendCallSignal(c.id, c.threadId, p.userId, 'camera-paused').catch(() => {}));
  }, [patch, flash]); // eslint-disable-line react-hooks/exhaustive-deps

  const flipCamera = useCallback(async () => {
    const c = callRef.current;
    if (!c?.video) return;
    const facing = c.facing === 'user' ? 'environment' : 'user';
    try {
      const cam = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { exact: facing } } });
      await meshRef.current?.setVideoTrack(cam.getVideoTracks()[0]);
      patch({ facing });
    } catch { flash('ONLY ONE CAMERA HERE'); }
  }, [patch, flash]);

  const toggleSpeaker = useCallback(() => {
    const c = callRef.current;
    if (!c) return;
    const on = !c.speaker;
    patch({ speaker: on });
    // The phone routes it for real (AVAudioSession / AudioManager); a browser has no say.
    nativeCall.setSpeaker(on);
  }, [patch]);

  const allowPerm = useCallback(async () => {
    const c = callRef.current;
    if (!c || c.phase !== 'perm') return;
    markCard(PERM_KEY);
    if (c.then === 'accept') { await join(c); return; }
    if (c.then === 'camera') { patch({ phase: c.resumePhase || 'live' }); await turnCameraOn(c); return; }
    patch({ phase: 'calling' });
    await dial(c);
  }, [patch]); // eslint-disable-line react-hooks/exhaustive-deps

  const denyPerm = useCallback(() => {
    const c = callRef.current;
    if (!c || c.phase !== 'perm') return;
    if (c.then === 'camera') { patch({ phase: c.resumePhase || 'live' }); flash('CAMERA STAYS OFF'); return; }
    if (c.then === 'accept') { decline(); flash(PERM_DENIED_TOAST); return; }
    setCall(null); flash(PERM_DENIED_TOAST);
  }, [patch, decline, flash]);

  const minimize = useCallback(() => {
    const c = callRef.current;
    if (!c) return;
    if (c.phase !== 'live' && c.phase !== 'connecting' && c.phase !== 'ringing' && c.phase !== 'calling') { hangup(); return; }
    patch({ view: 'mini', adding: false });
  }, [patch, hangup]);

  const restore = useCallback((originEl = null) => {
    originRef.current = originEl ? rectCentre(originEl) : null;
    patch({ view: 'full' });
  }, [patch]);

  const setCorner = useCallback((corner) => {
    patch({ miniPos: { corner } });
    try { localStorage.setItem(CORNER_KEY, corner); } catch { /* private mode */ }
  }, [patch]);

  /** ADD TO CALL: ring more people into this call. */
  const addPeople = useCallback(async (people) => {
    const c = callRef.current;
    if (!c || !people?.length) return;
    const selfId = profileRef.current?.userId;
    if (!selfId) { flash('PROFILE IS STILL LOADING · TRY AGAIN'); return; }
    if (people.some((p) => !p?.userId)) { flash('COULDN’T FIND WHO TO CALL · TRY AGAIN'); return; }
    const { room, full } = roomFor(c);
    if (room <= 0) { flash(full); return; }
    const picked = people.slice(0, room).filter((p) => !c.peers.some((x) => x.userId === p.userId));
    if (!picked.length) return;
    patch((cc) => ({ adding: false, peers: [...cc.peers, ...picked.map((p) => ({ userId: p.userId, name: p.name || p.displayName || '', avatarUrl: p.avatarUrl || null, state: 'ringing', level: 0, cameraPaused: false }))] }));
    try {
      await sendCallInvite(c.id, c.threadId, c.video, picked.map((p) => p.userId), [selfId, ...c.peers.map((p) => p.userId), ...picked.map((p) => p.userId)]);
    } catch (e) {
      flash((e?.message || 'COULDN’T ADD THEM').toUpperCase().replace(/^.*HUBEXCEPTION: /, ''));
      patch((cc) => ({ peers: cc.peers.filter((p) => !picked.some((x) => x.userId === p.userId)) }));
    }
  }, [patch, flash]);

  // ── The OS and the call agree: connected once, the notification's timer every ten seconds. ──
  const toldLive = useRef(null);
  useEffect(() => {
    const c = call;
    if (!c || c.phase !== 'live' || !c.startedAt) { if (!c) toldLive.current = null; return; }
    const one = c.peers.find((p) => p.state !== 'left') || c.peers[0];
    const name = c.peers.filter((p) => p.state !== 'left').length > 1 ? `GROUP ${c.video ? 'VIDEO' : 'CALL'} · ${c.peers.length + 1}` : one?.name || 'Portal';
    if (toldLive.current !== c.id) {
      toldLive.current = c.id;
      nativeCall.connected({ callId: c.id, name, video: c.video, muted: c.muted, speaker: c.speaker });
      if (c.video) nativeCall.setSpeaker(true);
      return;
    }
    const sec = Math.floor((now - c.startedAt) / 1000);
    if (sec % 10 === 0 || c.muted !== toldLive.muted || c.speaker !== toldLive.speaker) {
      toldLive.muted = c.muted; toldLive.speaker = c.speaker;
      nativeCall.update({ callId: c.id, name, video: c.video, muted: c.muted, speaker: c.speaker, timer: fmtTimer(c.startedAt, now) });
    }
  }, [call, now]);

  // ── The OS speaking: CallKit / the notifications, and a call it drew before we were up. ──
  useEffect(() => {
    if (!isAuthenticated || !myId || !nativePlatform()) return undefined;
    const offToken = registerVoipToken();
    const off = onNativeCall({
      incoming: (inv, options) => { if (inv) incoming(inv, options); },
      answer: (callId) => {
        const c = callRef.current;
        if (callId || c?.id) setNativeAnswerId(callId || c.id);
      },
      end: (callId) => {
        const c = callRef.current;
        if (!c || (callId && c.id !== callId)) return;
        if (c.phase === 'incoming' || (c.phase === 'perm' && c.then === 'accept')) decline(); else finish({ reason: 'hangup' });
      },
      mute: (muted) => setMutedFromNative(muted),
      speaker: () => toggleSpeaker(),
      open: () => { const c = callRef.current; if (c && c.view === 'mini') restore(); },
      remoteEnd: (callId) => {
        const c = callRef.current;
        if (c && c.id === callId && c.phase !== 'live') { teardown(); setCall(null); }
      },
    });
    // A call the OS handled while we were loading: ring it now, answer it if they already did.
    (async () => {
      const pending = await getPendingNative();
      if (pending.ended) return;
      if (pending.invite) {
        if (!callRef.current) incoming(pending.invite, { answered: pending.answered });
        if (pending.answered) setNativeAnswerId(pending.invite.callId);
      }
    })();
    return () => { off(); offToken(); };
  }, [isAuthenticated, myId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Background: the camera pauses while the app is hidden; audio keeps going. ──
  useEffect(() => {
    const onVis = () => {
      const c = callRef.current;
      if (!c?.video || !c.camOn) return;
      const hidden = document.visibilityState === 'hidden';
      localStreamRef.current?.getVideoTracks().forEach((t) => { t.enabled = !hidden; });
      c.peers.forEach((p) => sendCallSignal(c.id, c.threadId, p.userId, hidden ? 'camera-paused' : 'camera-resumed').catch(() => {}));
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Closing the tab ends the call: say so while one is live.
  useEffect(() => {
    const onUnload = (e) => { if (callRef.current?.phase === 'live') { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);

  useEffect(() => () => { teardown(); clearTimeout(toastTimer.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const me = useMemo(() => ({ displayName: profile?.displayName || '', avatarUrl: profile?.avatarUrl || profile?.faceUrl || null }), [profile?.displayName, profile?.avatarUrl, profile?.faceUrl]);
  const value = useMemo(() => ({
    call, now, toast, myId, me,
    streams: streamsRef.current, streamVersion, localStream: localStreamRef.current,
    origin: originRef.current, anchorRef,
    start, accept, decline, hangup, toggleMute, toggleCamera, flipCamera, toggleSpeaker, allowPerm, denyPerm, minimize, restore, setCorner, addPeople,
    openAdd: () => patch({ adding: true }), closeAdd: () => patch({ adding: false }),
    flash,
  }), [call, now, toast, myId, me, streamVersion, start, accept, decline, hangup, toggleMute, toggleCamera, flipCamera, toggleSpeaker, allowPerm, denyPerm, minimize, restore, setCorner, addPeople, patch, flash]);

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

function safeParse(s) { try { return JSON.parse(s); } catch { return null; } }
function readCorner() { try { return localStorage.getItem(CORNER_KEY) || 'tr'; } catch { return 'tr'; } }
function rectCentre(el) {
  const r = el?.getBoundingClientRect?.();
  return r ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : null;
}
